import { useState, useCallback, useRef, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { pilotService } from '../services/pilotService';

/**
 * Hook Pilot avec persistance Supabase.
 *
 * projectId et projectName sont optionnels : sans projet ouvert, Pilot
 * fonctionne en mode organisationnel (recherche dans tous les projets).
 * `enabled` faux (utilisateur sans acces a Pilot) : rien n'est lu en base.
 *
 * Les messages persistent entre navigations, refresh et reconnexions.
 * Seul startNewConversation() réinitialise la discussion.
 */
export function useCopilot(projectId, projectName, enabled = true) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const abortRef = useRef(null);

  // --- Chargement initial : conversation active + messages ---
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const init = async () => {
      try {
        const conv = await pilotService.getActiveConversation();
        if (cancelled) return;
        if (!conv) {
          // Aucune conversation existante — on en créera une au premier message
          return;
        }
        setConversationId(conv.id);
        const msgs = await pilotService.getMessages(conv.id);
        if (cancelled) return;
        setMessages(msgs.map(m => ({
          role: m.role,
          content: m.content,
          projectId: m.project_id,
          projectName: m.project_name,
          toolsUsed: m.tools_used || [],
          timestamp: new Date(m.created_at).getTime(),
        })));
      } catch (err) {
        console.error('[Pilot] init:', err?.message);
      } finally {
        if (!cancelled) setIsInitialized(true);
      }
    };
    init();
    return () => { cancelled = true; };
  }, [enabled]);

  // --- Envoi d'un message ---
  const sendMessage = useCallback(async (text) => {
    if (!text?.trim()) return;

    const currentProjectName = projectName || null;
    const currentProjectId = projectId || null;

    const userMsg = {
      role: 'user',
      content: text,
      projectId: currentProjectId,
      projectName: currentProjectName,
      timestamp: Date.now(),
    };
    // Message et attente affiches AVANT la creation de la conversation : le
    // champ est desactive tout de suite, un double Entree n'en cree pas deux.
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);
    setError(false);

    const controller = new AbortController();
    abortRef.current = controller;

    let convId = conversationId;

    // Créer la conversation au premier message si nécessaire
    if (!convId) {
      try {
        const conv = await pilotService.createConversation();
        // Nouvelle conversation demandee pendant la creation : on s'arrete la.
        if (abortRef.current !== controller) return;
        convId = conv.id;
        setConversationId(conv.id);
      } catch (err) {
        console.error('[Pilot] create conversation:', err?.message);
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsLoading(false);
          setError(true);
        }
        return;
      }
    }

    // Sauvegarder le message utilisateur dans Supabase (fire-and-forget)
    pilotService.addMessage(convId, {
      role: 'user',
      content: text,
      projectId: currentProjectId,
      projectName: currentProjectName,
    }).catch(err => console.error('[Pilot] save user msg:', err?.message));

    // Auto-titre : le premier message devient le titre de la conversation
    if (messages.length === 0) {
      const title = text.length > 60 ? text.slice(0, 57) + '…' : text;
      pilotService.updateConversationTitle(convId, title).catch(() => {});
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No session');

      // 10 derniers messages pour le contexte LLM
      const history = messages
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content }));

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const body = { message: text, history };
      if (currentProjectId) body.project_id = currentProjectId;

      const resp = await fetch(`${supabaseUrl}/functions/v1/pilot-chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody?.error || `HTTP ${resp.status}`);
      }

      const data = await resp.json();
      // Nouvelle conversation demandee pendant l'attente : la reponse est ignoree.
      if (abortRef.current !== controller) return;
      const assistantMsg = {
        role: 'assistant',
        content: data?.reply || '',
        projectId: currentProjectId,
        projectName: currentProjectName,
        toolsUsed: data?.tools_used || [],
        actions: data?.actions || [],
        timestamp: Date.now(),
      };
      setMessages(prev => [...prev, assistantMsg]);

      // Sauvegarder la réponse assistant dans Supabase
      pilotService.addMessage(convId, {
        role: 'assistant',
        content: assistantMsg.content,
        projectId: currentProjectId,
        projectName: currentProjectName,
        toolsUsed: assistantMsg.toolsUsed,
      }).catch(err => console.error('[Pilot] save assistant msg:', err?.message));

    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('[Pilot] sendMessage:', err?.message);
      if (abortRef.current === controller) setError(true);
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setIsLoading(false);
      }
    }
  }, [projectId, projectName, messages, conversationId]);

  // --- Nouvelle conversation (reset volontaire) ---
  const startNewConversation = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setIsLoading(false);
    setError(false);
    setMessages([]);
    setConversationId(null);
    // La prochaine fois que l'utilisateur envoie un message,
    // une nouvelle conversation sera créée automatiquement.
  }, []);

  return {
    messages,
    sendMessage,
    isLoading,
    error,
    startNewConversation,
    isInitialized,
  };
}

export default useCopilot;
